import { z } from 'zod';
import type { IconName } from '@/components/Icon/iconMap';
import type { TestCaseDetailEvaluation, TestCaseDetailItem } from '../-query/type';

/**
 * テスト項目(明細)台帳のカラム ID。
 *
 * accessorKey を持たない派生列(対象 / 連動不具合)も含め、列の参照を
 * 文字列リテラルの散在ではなくこの enum に集約する。
 */
export const TestItemsTableColumns = {
  CONDITION: 'conditionTitle',
  TARGET: 'target',
  PROCEDURE: 'procedure',
  EXPECTED_RESULT: 'expectedResult',
  CASE_TYPE: 'caseType',
  EVALUATION: 'evaluation',
  AI_EXECUTION: 'aiExecution',
  LINKED_ISSUES: 'linkedIssues'
} as const;

/** 評価結果の全ドメイン値(OK / NG / SKIP / BLOCKED / NOT_EXECUTED)。 */
const EVALUATION_VALUES = ['NOT_EXECUTED', 'OK', 'NG', 'SKIP', 'BLOCKED'] as const satisfies readonly TestCaseDetailEvaluation[];

/** 評価結果タブのフィルタ値。"ALL" + 5 評価値。 */
export const TestCaseEvaluationFilterSchema = z.enum(['ALL', ...EVALUATION_VALUES]);
export type TestCaseEvaluationFilter = z.infer<typeof TestCaseEvaluationFilterSchema>;

/**
 * 評価結果タブの並び順。
 *   すべて / 未実施 / 合格 / 不合格 / スキップ / ブロック
 */
export const EVALUATION_TABS = ['ALL', 'NOT_EXECUTED', 'OK', 'NG', 'SKIP', 'BLOCKED'] as const satisfies readonly TestCaseEvaluationFilter[];

/** 評価結果タブごとのアイコン。"ALL" はアイコンなし。 */
export const EVALUATION_TAB_TO_ICON: Record<TestCaseEvaluationFilter, IconName | null> = {
  ALL: null,
  NOT_EXECUTED: 'Clock2Icon',
  OK: 'CircleCheckIcon',
  NG: 'CircleXIcon',
  SKIP: 'FastForwardIcon',
  BLOCKED: 'XOctagonIcon'
};

/** 評価結果タブごとの件数(ロード済みデータから算出)。 */
export type TestItemsEvaluationCounts = Record<TestCaseEvaluationFilter, number>;

/**
 * ロード済みの全明細から、各評価結果タブの件数を算出する。
 * "ALL" は総件数。検索による絞り込みは考慮しない(タブ件数は常に総数基準)。
 */
export function countByEvaluation(items: readonly TestCaseDetailItem[]): TestItemsEvaluationCounts {
  const counts: TestItemsEvaluationCounts = {
    ALL: items.length,
    NOT_EXECUTED: 0,
    OK: 0,
    NG: 0,
    SKIP: 0,
    BLOCKED: 0
  };
  for (const item of items) {
    counts[item.evaluation] += 1;
  }
  return counts;
}

/**
 * 明細を 評価結果タブ + freeText で client-side 絞り込みする。
 * freeText は 対象(target / targetNodeName)/ 手順 / 期待値 への
 * 大文字小文字を無視した部分一致。
 */
export function filterTestItems(items: readonly TestCaseDetailItem[], status: TestCaseEvaluationFilter, freeText: string | undefined): TestCaseDetailItem[] {
  const keyword = freeText?.trim().toLowerCase();
  return items.filter(item => {
    if (status !== 'ALL' && item.evaluation !== status) return false;
    if (!keyword) return true;
    const haystack = [item.target, item.targetNodeName, item.procedure, item.expectedResult].filter((value): value is string => Boolean(value)).join(' ').toLowerCase();
    return haystack.includes(keyword);
  });
}